import { useState } from 'react';
import JSZip from 'jszip';
import type { DocDocument, DocBrand, LibraryScore } from '../types/docops';
import { COMPLETED_STATUSES } from '../utils/docopsPipeline';

interface UseDocExportParams {
  documents: DocDocument[];
  activeBrand: DocBrand | null;
  libraryScore: LibraryScore | null;
}

export function useDocExport({ documents, activeBrand, libraryScore }: UseDocExportParams) {
  const [exporting, setExporting] = useState(false);

  const downloadMarkdown = (doc: DocDocument) => {
    const blob = new Blob([doc.content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${doc.title.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadHTML = (doc: DocDocument) => {
    const html = `<!DOCTYPE html>
<html><head>
<meta charset="utf-8">
<title>${doc.title}</title>
<style>
  body { font-family: 'Georgia', serif; max-width: 800px; margin: 40px auto; padding: 0 20px; color: #333; line-height: 1.7; }
  h1 { font-size: 24px; border-bottom: 2px solid #333; padding-bottom: 8px; }
  h2 { font-size: 18px; margin-top: 24px; color: #222; }
  h3 { font-size: 15px; margin-top: 16px; }
  table { border-collapse: collapse; width: 100%; margin: 12px 0; }
  th, td { border: 1px solid #ccc; padding: 6px 10px; text-align: left; font-size: 13px; }
  th { background: #f5f5f5; }
  @media print { body { margin: 20px; } }
</style>
</head><body>
${doc.content.replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>')}
</body></html>`;
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${doc.title.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadAllDocs = async () => {
    const generated = documents.filter(d => d.content && d.status !== 'not_started');
    if (generated.length === 0) return;

    setExporting(true);
    try {
      const zip = new JSZip();

      const categoryNames: Record<string, string> = {
        'user-facing-legal': '01-User-Facing-Legal',
        'corporate-formation': '02-Corporate-Formation',
        'trust-ip': '03-Trust-IP',
        'mso-pc': '04-MSO-PC-Documents',
        'hipaa': '05-HIPAA-Compliance',
        'vendor-partner': '06-Vendor-Partner',
        'employment': '07-Employment',
        'compliance-ops': '08-Compliance-Operations',
        'dashboards': '09-Dashboards-Tools',
        'business-planning': '10-Business-Planning',
        'acquisition-data-room': '11-Acquisition-Data-Room',
        'clinical': '12-Clinical',
      };

      for (const doc of generated) {
        const folder = categoryNames[doc.categoryId] || 'Other';
        const fileName = `${doc.templateId}-${doc.title.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-').toLowerCase()}.md`;
        const header = `# ${doc.title}\n\n**Status:** ${doc.status} | **Score:** ${doc.auditScore ?? 'N/A'}% | **Version:** v${doc.version}\n**Generated:** ${doc.generatedAt || 'N/A'} | **Last Edited:** ${doc.lastEditedAt || 'N/A'}\n${doc.launchBlocker ? '**LAUNCH BLOCKER**' : ''}\n${doc.attorneyReviewRequired ? '**Attorney Review Required**' : ''}\n\n---\n\n`;
        zip.file(`${folder}/${fileName}`, header + doc.content);
      }

      const notStarted = documents.filter(d => d.status === 'not_started').length;
      const blockers = documents.filter(d => d.launchBlocker);
      const blockersComplete = blockers.filter(d => COMPLETED_STATUSES.has(d.status)).length;

      const summary = `# Hemologix Document Library — Attorney Review Package

**Brand:** ${activeBrand?.config?.brandName || 'N/A'}
**Entity:** ${activeBrand?.config?.brandEntity || 'N/A'}
**Export Date:** ${new Date().toLocaleDateString()}

## Library Status

| Metric | Value |
|--------|-------|
| Total Templates | ${documents.length} |
| Generated | ${generated.length} |
| Not Started | ${notStarted} |
| Launch Blockers | ${blockersComplete}/${blockers.length} complete |
| Library Score | ${libraryScore?.combinedScore ?? 'N/A'}% |
| Critical Conflicts | ${libraryScore?.criticalConflicts ?? 'N/A'} |
| Launch Ready | ${libraryScore?.passed ? 'YES' : 'NO'} |

## Quality Scores by Document

| Document | Score | Status | Launch Blocker | Attorney Review |
|----------|-------|--------|----------------|-----------------|
${generated.sort((a, b) => (a.templateId > b.templateId ? 1 : -1)).map(d => `| ${d.title} | ${d.auditScore ?? 'N/A'}% | ${d.status} | ${d.launchBlocker ? 'YES' : ''} | ${d.attorneyReviewRequired ? 'YES' : ''} |`).join('\n')}

## Cross-Document Conflicts

${libraryScore?.conflicts.filter(c => c.status !== 'pending').length === 0 ? 'No active conflicts.' : libraryScore?.conflicts.filter(c => c.status !== 'pending').map(c => `- **[${c.severity.toUpperCase()}]** ${c.description}`).join('\n')}

## Notes for Attorney

1. Documents marked **LAUNCH BLOCKER** must be reviewed and approved before platform launch.
2. Documents marked **Attorney Review Required** need legal sign-off.
3. All documents were AI-generated using the Hemologix Doc Ops Center and have passed automated quality gates.
4. Cross-document consistency (entity names, defined terms, provisions) has been validated by the scoring engine.
5. Template variables ({{variableName}}) that remain indicate missing brand configuration data.

---
*Generated by Hemologix Doc Ops Center*
`;
      zip.file('00-COVER-SUMMARY.md', summary);

      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `hemologix-document-library-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  return { exporting, downloadMarkdown, downloadHTML, downloadAllDocs };
}
